import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiDatabase } from 'react-icons/fi';
import AppModal from './common/AppModal';
import { formatApiError } from '../utils/formatters';
import './BuiltinConnectionDialog.css';

const API = process.env.REACT_APP_API_BASE_URL || '/sql-developer';

/**
 * 내장 저장소 연결(monitoring.db) 편집(2026-10-28 사용자 요청) — 이름 · 설명 · 읽기 전용만.
 * 파일 경로는 고치지 않는다(앱 저장물 전부라 경로를 바꾸면 앱이 다른 파일을 본다).
 * 읽기 전용을 꺼도 쓸 수 있는 것은 usr_ 로 시작하는 사용자 테이블뿐이다 — 판정은 서버(sqliteAdapter)가 한다.
 */
const BuiltinConnectionDialog = ({ open, connection, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [readOnly, setReadOnly] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open || !connection) return;
        setName(connection.name || '');
        setDescription(connection.description || '');
        setReadOnly(connection.readOnly !== false);
    }, [open, connection]);

    const save = async () => {
        if (!connection) return;
        setBusy(true);
        try {
            const res = await axios.put(`${API}/connections/${encodeURIComponent(connection.id)}`, { name: name.trim(), description, readOnly });
            toast.success(t('connection:builtin.saved'));
            if (onSaved) onSaved(res.data);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err, t));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AppModal open={open} onClose={onClose} title={t('connection:builtin.title')} icon={<FiDatabase size={14} />} className="app-modal-default bic-modal">
            <div className="bic-body">
                <label className="bic-field">
                    <span>{t('connection:builtin.name')}</span>
                    <input type="text" value={name} maxLength={80} onChange={e => setName(e.target.value)} />
                </label>
                <label className="bic-field">
                    <span>{t('connection:builtin.description')}</span>
                    <textarea rows={2} value={description} maxLength={300} onChange={e => setDescription(e.target.value)} />
                </label>
                <label className="bic-check">
                    <input type="checkbox" checked={readOnly} onChange={e => setReadOnly(e.target.checked)} />
                    <span>{t('connection:builtin.readOnly')}</span>
                </label>
                <div className={`bic-note ${readOnly ? '' : 'bic-note-warn'}`}>
                    {readOnly ? t('connection:builtin.readOnlyHint') : t('connection:builtin.writableHint')}
                </div>
                <div className="bic-path">{t('connection:builtin.pathFixed')}</div>
            </div>
            <div className="bic-foot">
                <button type="button" className="app-btn" onClick={onClose} disabled={busy}>{t('common:action.cancel')}</button>
                <button type="button" className="app-btn app-btn-primary" onClick={save} disabled={busy || !name.trim()}>{t('common:action.save')}</button>
            </div>
        </AppModal>
    );
};

BuiltinConnectionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    connection: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
};

export default BuiltinConnectionDialog;
